import { captureException } from '@sentry/react';
import { AxiosResponse } from 'axios';
import { StateCreator } from 'zustand';

import { authorizedAxiosInstance } from '@/clients';

import { DatasetActions, DatasetState } from '../types';

const AUTO_QUERY_ERROR_COOLDOWN_MS = 30_000;

const canonicalizeDatasetRequestValue = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(canonicalizeDatasetRequestValue);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = canonicalizeDatasetRequestValue((value as Record<string, unknown>)[key]);

            return acc;
        }, {});
};

const getDatasetRequestKey = (componentId: string, query: unknown) =>
    JSON.stringify({
        componentId,
        configuration: canonicalizeDatasetRequestValue(query),
    });

export const createDatasetActions: StateCreator<
    DatasetState & DatasetActions,
    [['zustand/immer', never]],
    [],
    DatasetActions
> = (set, get, store) => ({
    // Core
    fetchDatasets: async (params) => {
        set({ loading: true });

        try {
            const res: AxiosResponse = await authorizedAxiosInstance.get('/api/datasets', { params });

            if (res.status === 200) {
                set({ datasets: res.data, loading: false, error: undefined });
            }
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load datasets' });
        }
    },
    // Aliases
    queryDatasets: async (componentId, query, requestKey, options) => {
        if (!query) {
            return undefined;
        }

        const force = Boolean(options?.force);
        const existingQueryState = get().componentQueryState[componentId];
        const nextRequestKey = requestKey ?? getDatasetRequestKey(componentId, query);

        if (existingQueryState?.loading && existingQueryState.requestKey === nextRequestKey) {
            return existingQueryState.data;
        }

        if (
            !force &&
            existingQueryState?.requestKey === nextRequestKey &&
            existingQueryState.data &&
            !existingQueryState.error
        ) {
            return existingQueryState.data;
        }

        if (
            !force &&
            existingQueryState?.requestKey === nextRequestKey &&
            existingQueryState.error &&
            existingQueryState.lastFetchedAt &&
            Date.now() - existingQueryState.lastFetchedAt < AUTO_QUERY_ERROR_COOLDOWN_MS
        ) {
            return existingQueryState.data;
        }

        set((state) => {
            state.componentQueryState[componentId] = {
                ...state.componentQueryState[componentId],
                loading: true,
                error: undefined,
                requestKey: nextRequestKey,
            };
        });

        try {
            const res = await authorizedAxiosInstance.post('/api/datasets/query', { configuration: query });

            set((state) => {
                state.componentQueryState[componentId] = {
                    loading: false,
                    data: res.data,
                    lastFetchedAt: Date.now(),
                    requestKey: nextRequestKey,
                };
            });

            return res.data;
        } catch (error) {
            captureException(error);

            set((state) => {
                state.componentQueryState[componentId] = {
                    ...state.componentQueryState[componentId],
                    loading: false,
                    error: 'Failed to load datasets',
                    lastFetchedAt: Date.now(),
                    requestKey: nextRequestKey,
                };
            });

            return undefined;
        }
    },
    invalidateComponentQuery: (componentId) => {
        set((state) => {
            delete state.componentQueryState[componentId];
        });
    },
});
